/**
 * Composable for generating cross-site URLs in a multi-tenant environment
 * Handles domain resolution for different environments (local, staging, production)
 */
export const useCrossSiteUrl = () => {
  const runtimeConfig = useRuntimeConfig()
  const currentTenant = useTenant()

  /**
   * Generates an absolute URL for a different site/tenant
   * @param domainId - The domain ID from the location (either 'equans' or 'kuma')
   * @param langcode - Language code for the URL
   * @param path - The path/alias to append
   * @returns Complete absolute URL
   */
  const getCrossSiteUrl = (domainId: string, langcode: string, path: string): string => {
    // Use the domain ID directly as the target site
    const targetSite = domainId

    // If we're already on the correct site, return relative URL
    if (currentTenant === targetSite) {
      const cleanPath = path.startsWith('/') ? path : `/${path}`
      return `/${langcode}${cleanPath}`
    }

    // Get current host to determine environment
    const currentHost: string = process.client ? window.location.host : runtimeConfig.requestHost || ''

    let targetDomain: string

    // Production domains (hardcoded as per config)
    if (currentHost.includes('equans.ch') || currentHost.includes('kuma-evt.ch')) {
      targetDomain = targetSite === 'kuma' ? 'https://kuma-evt.ch' : 'https://equans.ch'
    }
    // Local development (ddev)
    else if (currentHost.includes('.ddev.site')) {
      targetDomain = `https://${targetSite}.ddev.site`
    }
    // Platform.sh staging/preview environments
    else if (currentHost.includes('.platformsh.site')) {
      // Extract the platform.sh environment identifier - more flexible pattern
      const platformshPattern = /^([^.]+)\.(.+\.platformsh\.site)$/
      const match = currentHost.match(platformshPattern)

      if (match) {
        const envDomain = match[2] // e.g., "pr-123-xxxxxxx-yyyyyyyyyyyyy.ch-1.platformsh.site" or "main-bvxea6i-yaugup2ejykzs.us-2.platformsh.site"
        targetDomain = `https://${targetSite}.${envDomain}`
      } else {
        // Fallback for main branch or other platform.sh patterns
        const fallbackPattern = /^[^.]+\.(.+\.platformsh\.site)$/
        const fallbackMatch = currentHost.match(fallbackPattern)
        if (fallbackMatch) {
          targetDomain = `https://${targetSite}.${fallbackMatch[1]}`
        } else {
          // Ultimate fallback - use current host pattern but replace subdomain
          targetDomain = `https://${targetSite}.${currentHost}`
        }
      }
    }
    // Fallback for unknown environments
    else {
      targetDomain = `https://${targetSite}.ddev.site`
    }

    // Clean up the path (ensure it starts with /)
    const cleanPath = path.startsWith('/') ? path : `/${path}`

    // Build the complete URL with language prefix
    return `${targetDomain}/${langcode}${cleanPath}`
  }

  /**
   * Determines if a location requires a cross-site URL
   * @param domainId - The domain ID from the location
   * @param currentTenantId - The current tenant/site ID
   * @returns True if this location should use a different domain
   */
  const requiresCrossSiteUrl = (domainId: string, currentTenantId: string): boolean => {
    // Only generate cross-site URLs if we have a valid domain ID and it's different from current tenant
    return !!domainId && ['equans', 'kuma'].includes(domainId) && domainId !== currentTenantId
  }

  return {
    getCrossSiteUrl,
    requiresCrossSiteUrl
  }
}
